import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { listDdtPractices, fetchDdtCount, fetchDdtSuggestions, checkNewDdt } from "../../api/client";
import { StatusIcon } from "../../lib/status";
import { LockChip } from "../../lib/LockChip";
import { ActiveFiltersBar, type FiltroAttivo } from "../../lib/ActiveFiltersBar";
import { correctedFieldLabel } from "../../lib/activityLabels";
import type { DdtPractice, DdtSuggestion, DdtSortKey } from "../../api/types";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100];
const REFRESH_INTERVAL = 30_000;

const STATUS_OPTIONS = [
  { value: "all", label: "Tutte" },
  { value: "pending_review", label: "Da rivedere" },
  { value: "approved", label: "Approvate" },
  { value: "rejected", label: "Rifiutate" },
  { value: "no_pdf", label: "Senza PDF" },
  { value: "error", label: "Errore" },
];

// Tipo documento (ADR 0005) — tag informativo ortogonale allo status.
const DOCUMENT_TYPE_OPTIONS = [
  { value: "", label: "Tutti" },
  { value: "ddt", label: "DDT" },
  { value: "fattura", label: "Fattura" },
  { value: "non_ddt", label: "Non DDT" },
];

const DOCUMENT_TYPE_BADGE: Record<string, { label: string; className: string }> = {
  ddt:     { label: "DDT",     className: "badge badge-success" },
  fattura: { label: "Fattura", className: "badge badge-warning" },
  non_ddt: { label: "Non DDT", className: "badge badge-neutral" },
};

function getDocumentTypeBadge(t: string | undefined) {
  const c = DOCUMENT_TYPE_BADGE[t ?? "ddt"] ?? DOCUMENT_TYPE_BADGE.ddt;
  return <span className={c.className} style={{ fontSize: 10, padding: "1px 6px" }}>{c.label}</span>;
}

// Resa stato unificata → lib/status
function getStatusIcon(s: string) {
  return <StatusIcon status={s} />;
}

function completenessColor(v: number) {
  if (v >= 0.75) return "var(--indaco-success)";
  if (v >= 0.50) return "var(--indaco-warning)";
  return "var(--indaco-danger)";
}

function fornitorePkSimColor(v: number) {
  if (v >= 0.85) return "var(--indaco-success)";
  if (v >= 0.55) return "var(--indaco-warning)";
  return "var(--indaco-danger)";
}

function ddtCompleteness(ddt: DdtPractice["ddt_data"]): number {
  if (!ddt) return 0;
  const fields = [ddt.ordine_acquisto, ddt.numero_ddt, ddt.data_ora, ddt.fornitore_testo];
  const nonNull = fields.filter((f) => f !== null && f !== undefined && f !== "").length;
  return nonNull / 4;
}

function formatDate(iso: string) {
  try {
    const d = new Date(iso);
    const dayMonth = d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
    const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return `${dayMonth} ${time}`;
  } catch { return iso; }
}

function formatDateFull(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export function DdtList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const goToPractice = (id: string) => navigate(`/ddt/${id}`, { state: { fromQuery: location.search } });

  // ── Lettura filtri da URL ──
  const status = searchParams.get("status") || "pending_review";
  const fId = searchParams.get("id") || "";
  const fPdf = searchParams.get("pdf_filename") || "";
  const fNumeroDdt = searchParams.get("numero_ddt") || "";
  const fOda = searchParams.get("ordine_acquisto") || "";
  const fFornitore = searchParams.get("fornitore") || "";
  const fFornitorePk = searchParams.get("fornitore_pk") || "";
  const fDocumentType = searchParams.get("document_type") || "";
  const fQ = searchParams.get("q") || "";
  // Filtri di periodo e correzioni: sono i drill-down della pagina statistiche.
  // Senza la lettura da URL i link arriverebbero qui e non filtrerebbero nulla.
  const fDateFrom = searchParams.get("date_from") || "";
  const fDateTo = searchParams.get("date_to") || "";
  const fHadCorrections = searchParams.get("had_corrections") || "";
  const fCorrectedField = searchParams.get("corrected_field") || "";
  const sort = searchParams.get("sort") || "date_desc";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = parseInt(searchParams.get("size") || String(DEFAULT_PAGE_SIZE), 10);

  const [data, setData] = useState<{ items: DdtPractice[]; total: number } | null>(null);
  const [counts, setCounts] = useState<{ pending: number; approvedToday: number; total: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [secondsAgo, setSecondsAgo] = useState(0);
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState<string | null>(null);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // ── Search bar state ──
  const [searchText, setSearchText] = useState(fQ);
  const [suggestions, setSuggestions] = useState<DdtSuggestion[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestIdx, setSuggestIdx] = useState(-1);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const suggestTimer = useRef<ReturnType<typeof setTimeout>>();
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [lastVisitedId, setLastVisitedId] = useState<string | null>(() => localStorage.getItem("lastVisitedDdtId"));

  useEffect(() => { setSearchText(fQ); }, [fQ]);

  // Close search dropdown on outside click
  useEffect(() => {
    if (!fQ && !searchText) return;
    function handleClick(e: MouseEvent) {
      if (searchContainerRef.current?.contains(e.target as Node)) return;
      setSuggestions([]);
    }
    const t = setTimeout(() => document.addEventListener("mousedown", handleClick), 0);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", handleClick); };
  }, [fQ, searchText]);

  function handleSearchInput(val: string) {
    setSearchText(val);
    setSuggestIdx(-1);
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (val.length < 2) { setSuggestions([]); return; }
    setSuggestLoading(true);
    suggestTimer.current = setTimeout(() => {
      fetchDdtSuggestions(val)
        .then(r => setSuggestions(r))
        .catch(() => setSuggestions([]))
        .finally(() => setSuggestLoading(false));
    }, 300);
  }

  function applySearch(q: string) {
    setSuggestions([]);
    setFilter({ q: q.trim() || null });
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      setSuggestions([]);
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setSuggestIdx(i => Math.min(i + 1, suggestions.length - 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setSuggestIdx(i => Math.max(i - 1, -1)); return; }
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestIdx >= 0 && suggestions[suggestIdx]) {
        goToPractice(suggestions[suggestIdx].id);
        setSuggestions([]);
      } else {
        applySearch(searchText);
      }
    }
  }

  // Close filter dropdown on outside click
  useEffect(() => {
    if (!openFilter) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (target.closest("[data-filter-popover]") || target.closest("[data-filter-toggle]")) return;
      setOpenFilter(null);
    }
    const timer = setTimeout(() => document.addEventListener("click", handleClick), 0);
    return () => { clearTimeout(timer); document.removeEventListener("click", handleClick); };
  }, [openFilter]);

  function setFilter(updates: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === "" || v === undefined) next.delete(k);
        else next.set(k, v);
      }
      if (!("page" in updates)) next.delete("page");
      return next;
    }, { replace: true });
  }

  function setTextFilter(key: string, value: string) {
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => setFilter({ [key]: value || null }), 300);
  }

  const loadCounts = useCallback(() => {
    fetchDdtCount()
      .then((c) => {
        setCounts({
          pending: c.pending_review,
          approvedToday: c.approved_today,
          total: c.total,
        });
      })
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setIsLoading(true);
    listDdtPractices({
      status: (status === "all" || !status) ? undefined : status,
      q: fQ || undefined,
      id: fId || undefined,
      pdf_filename: fPdf || undefined,
      numero_ddt: fNumeroDdt || undefined,
      ordine_acquisto: fOda || undefined,
      fornitore: fFornitore || undefined,
      fornitore_pk: fFornitorePk || undefined,
      document_type: fDocumentType || undefined,
      date_from: fDateFrom || undefined,
      date_to: fDateTo || undefined,
      had_corrections: fHadCorrections === "" ? undefined : fHadCorrections === "true",
      corrected_field: fCorrectedField || undefined,
      sort: sort as DdtSortKey,
      page,
      page_size: pageSize,
    })
      .then((d) => { setData({ items: d.items, total: d.total }); setError(null); setLastRefresh(Date.now()); })
      .catch((e) => setError(e instanceof Error ? e.message : "Errore caricamento"))
      .finally(() => setIsLoading(false));
  }, [status, fQ, fId, fPdf, fNumeroDdt, fOda, fFornitore, fFornitorePk, fDocumentType,
      fDateFrom, fDateTo, fHadCorrections, fCorrectedField, sort, page, pageSize]);

  // "Forza aggiornamento": forza subito l'ingestione della mailbox DDT (come
  // il tasto ricevi di un client email), poi ricarica lista + contatori.
  const handleCheckNew = useCallback(async () => {
    setChecking(true);
    setCheckMsg(null);
    try {
      const r = await checkNewDdt();
      setCheckMsg(r.error ? `Errore: ${r.error}` : r.new > 0 ? `${r.new} nuove pratiche` : "Nessuna nuova email");
      load();
      loadCounts();
    } catch {
      setCheckMsg("Errore nel controllo");
    } finally {
      setChecking(false);
      setTimeout(() => setCheckMsg(null), 5000);
    }
  }, [load, loadCounts]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => {
    const id = setInterval(() => { load(); loadCounts(); }, REFRESH_INTERVAL);
    return () => clearInterval(id);
  }, [load, loadCounts]);
  useEffect(() => {
    const id = setInterval(() => setSecondsAgo(Math.floor((Date.now() - lastRefresh) / 1000)), 1000);
    return () => clearInterval(id);
  }, [lastRefresh]);

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;
  const hasAnyColumnFilter = !!(fQ || fId || fPdf || fNumeroDdt || fOda || fFornitore || fFornitorePk || fDocumentType);

  // ── Column header con sort + filter popover (replica RDI) ──
  function ColHeader({ label, sortKey, filterKey, filterType, filterValue, options, style }:
    { label: string; sortKey: string; filterKey?: string; filterType?: "text" | "select";
      filterValue?: string; options?: { value: string; label: string }[]; style?: React.CSSProperties }) {
    const isAsc = sort === `${sortKey}_asc`;
    const isDesc = sort === `${sortKey}_desc`;
    const isSortActive = isAsc || isDesc;
    const nextSort = isAsc ? `${sortKey}_desc` : isDesc ? null : `${sortKey}_asc`;
    const isFiltered = filterKey ? Boolean(filterValue) : false;
    const isOpen = openFilter === sortKey;

    return (
      <th style={{ position: "relative", ...style }}>
        <div style={{ display: "flex", alignItems: "center", gap: 2, userSelect: "none" }}>
          <span style={{ cursor: "pointer", flex: 1 }} onClick={() => setFilter({ sort: nextSort })}>
            {label}
          </span>
          <span className={`material-symbols-outlined${isSortActive ? "" : " th-icon-hover"}`}
            onClick={() => setFilter({ sort: nextSort })}
            style={{ fontSize: 14, cursor: "pointer", padding: 2,
                     color: "var(--indaco-brand-primary)", opacity: isSortActive ? 1 : 0.6 }}>
            {isAsc ? "arrow_upward" : isDesc ? "arrow_downward" : "swap_vert"}
          </span>
          {filterKey && (
            <span className={`material-symbols-outlined${isFiltered ? "" : " th-icon-hover"}`}
              data-filter-toggle onClick={() => setOpenFilter(isOpen ? null : sortKey)}
              style={{ fontSize: 14, cursor: "pointer", padding: 2,
                       color: "var(--indaco-brand-primary)", opacity: isFiltered ? 1 : 0.6,
                       background: isFiltered ? "rgba(var(--indaco-brand-primary-rgb),0.1)" : "transparent",
                       borderRadius: 4 }}>
              filter_alt
            </span>
          )}
        </div>
        {isOpen && filterKey && (
          <div data-filter-popover
            style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, minWidth: 180,
                     background: "var(--indaco-white)", border: "1px solid var(--indaco-gray-200)", borderRadius: "var(--border-radius-lg)",
                     boxShadow: "var(--shadow-lg)", padding: "8px 10px", marginTop: 4 }}>
            {filterType === "select" && options && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {options.map((o) => (
                  <button key={o.value}
                    className={`btn btn-sm ${filterValue === o.value || (!filterValue && !o.value) ? "btn-primary" : "btn-ghost"}`}
                    style={{ justifyContent: "flex-start", fontSize: "var(--font-size-xs)", padding: "4px 8px" }}
                    onClick={() => { setFilter({ [filterKey]: o.value || null }); setOpenFilter(null); }}>
                    {o.label}
                  </button>
                ))}
              </div>
            )}
            {filterType === "text" && (
              <input autoFocus type="text" className="form-input"
                style={{ fontSize: "var(--font-size-xs)", padding: "4px 8px", width: "100%" }}
                placeholder={`Filtra ${label.toLowerCase()}...`}
                defaultValue={filterValue}
                onChange={(e) => setTextFilter(filterKey, e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setOpenFilter(null); }} />
            )}
            {filterValue && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 6, fontSize: "var(--font-size-xs)", width: "100%", color: "var(--indaco-danger)" }}
                onClick={() => { setFilter({ [filterKey]: null }); setOpenFilter(null); }}>
                <span className="material-symbols-outlined" style={{ fontSize: 13, marginRight: 4 }}>close</span>Rimuovi filtro
              </button>
            )}
          </div>
        )}
      </th>
    );
  }

  const title = "Pratiche DDT";

  return (
    <div style={{ padding: "var(--space-4)" }}>
      {/* Header */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-3)", gap: "var(--space-3)", rowGap: "var(--space-2)" }}>
        <h1 className="page-title" style={{ flexShrink: 0 }}>{title}</h1>

        {/* ── Search bar ── */}
        <div ref={searchContainerRef} style={{ position: "relative", flex: 1, maxWidth: 480, minWidth: 0 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            border: fQ ? "1.5px solid var(--indaco-brand-primary)" : "1px solid var(--indaco-gray-300)",
            borderRadius: "var(--border-radius-md)", background: fQ ? "var(--indaco-brand-primary-light)" : "var(--indaco-white)", padding: "5px 12px",
            boxShadow: suggestions.length > 0 ? "var(--shadow-md)" : "none",
            transition: "border-color .15s, background .15s, box-shadow .15s",
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: fQ ? "var(--indaco-brand-primary)" : "var(--indaco-gray-400)", flexShrink: 0 }}>search</span>
            <input type="text" value={searchText}
              placeholder="Cerca: numero DDT, ODA, fornitore, file PDF, ID..."
              onChange={e => handleSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => { if (searchText.length >= 2 && suggestions.length === 0) handleSearchInput(searchText); }}
              style={{ flex: 1, border: "none", outline: "none", background: "transparent",
                       fontSize: 13, padding: "3px 0", minWidth: 0 }} />
            {suggestLoading && <span className="spinner" style={{ width: 14, height: 14, flexShrink: 0 }} />}
            {(searchText || fQ) && (
              <button onClick={() => { setSearchText(""); applySearch(""); }}
                style={{ display: "flex", alignItems: "center", border: "none", background: "none", cursor: "pointer", padding: 2, flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-400)" }}>close</span>
              </button>
            )}
          </div>
          {/* Suggestions dropdown */}
          {suggestions.length > 0 && (
            <div style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 200,
              background: "var(--indaco-white)", border: "1px solid var(--indaco-gray-200)", borderRadius: "var(--border-radius-lg)",
              boxShadow: "var(--shadow-lg)", overflow: "hidden", maxHeight: 360, overflowY: "auto",
            }}>
              {suggestions.map((s, i) => (
                <div key={s.id}
                  onMouseDown={() => { goToPractice(s.id); setSuggestions([]); }}
                  onMouseEnter={() => setSuggestIdx(i)}
                  style={{
                    padding: "8px 12px", cursor: "pointer", fontSize: "var(--font-size-sm)",
                    background: i === suggestIdx ? "var(--indaco-gray-50)" : "transparent",
                    borderBottom: i < suggestions.length - 1 ? "1px solid var(--indaco-gray-100)" : "none",
                  }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 11, color: "var(--indaco-gray-400)", fontFamily: "monospace", flexShrink: 0 }}>
                      {s.id.slice(0, 8)}
                    </span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      {s.numero_ddt || s.pdf_filename || s.email_subject || "(senza dati)"}
                    </span>
                    {s.fornitore_pk && (
                      <code style={{ fontSize: 10, color: "var(--indaco-gray-500)", flexShrink: 0, fontFamily: "monospace" }}>{s.fornitore_pk}</code>
                    )}
                    {s.fornitore && (
                      <code style={{ fontSize: 10, color: "var(--indaco-gray-500)", flexShrink: 0 }}>{s.fornitore.slice(0, 30)}</code>
                    )}
                  </div>
                  {(s.email_subject || s.pdf_filename) && (
                    <div style={{ fontSize: 11, color: "var(--indaco-gray-400)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.pdf_filename || s.email_subject}
                    </div>
                  )}
                </div>
              ))}
              <div style={{ padding: "6px 12px", fontSize: 11, color: "var(--indaco-gray-400)", background: "var(--indaco-gray-50)", textAlign: "center" }}>
                Invio per cercare in tutti i risultati
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flexShrink: 0 }}>
          {hasAnyColumnFilter && (
            <button className="btn btn-ghost btn-sm" title="Pulisci tutti i filtri"
              onClick={() => { setFilter({ q: null, id: null, pdf_filename: null, numero_ddt: null, ordine_acquisto: null, fornitore: null, fornitore_pk: null, document_type: null }); setSearchText(""); }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-danger)" }}>filter_list_off</span>
            </button>
          )}
          {checkMsg && <span className="text-xs" style={{ color: "var(--indaco-brand-primary)" }}>{checkMsg}</span>}
          <button className="btn btn-outline btn-sm" onClick={handleCheckNew} disabled={checking}
            title="Controlla subito nuove email nella casella DDT (forza aggiornamento)"
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {checking
              ? <span className="spinner" style={{ width: 14, height: 14 }} />
              : <span className="material-symbols-outlined" style={{ fontSize: 16 }}>mark_email_unread</span>}
            <span>Forza aggiornamento</span>
          </button>
          <span className="text-muted text-xs">{secondsAgo < 5 ? "aggiornato ora" : `${secondsAgo}s fa`}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => { load(); loadCounts(); }} title="Aggiorna">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>refresh</span>
          </button>
        </div>
      </div>

      {/* Counters */}
      <div style={{ display: "flex", gap: "var(--space-4)", marginBottom: "var(--space-3)", fontSize: "var(--font-size-sm)", alignItems: "center", flexWrap: "wrap" }}>
        <span><span className="text-muted">Da rivedere: </span><strong style={{ color: "var(--indaco-warning)" }}>{counts?.pending ?? "—"}</strong></span>
        <span style={{ color: "var(--indaco-gray-300)" }}>|</span>
        <span><span className="text-muted">Approvati oggi: </span><strong style={{ color: "var(--indaco-success)" }}>{counts?.approvedToday ?? "—"}</strong></span>
        <span style={{ color: "var(--indaco-gray-300)" }}>|</span>
        <span><span className="text-muted">Totale: </span><strong>{counts?.total ?? "—"}</strong></span>
      </div>

      {/* Top bar: status pills */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--space-3)", flexWrap: "wrap", gap: "var(--space-2)" }}>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "center" }}>
          {STATUS_OPTIONS.map((opt) => (
            <button key={opt.value} className={`btn btn-sm ${status === opt.value ? "btn-primary" : "btn-outline"}`}
              onClick={() => setFilter({ status: opt.value === "all" ? null : opt.value })}>{opt.label}</button>
          ))}
        </div>
      </div>

      {/* Filtro arrivato da un drill-down delle statistiche: senza il banner la
          lista apparirebbe ridotta senza modo di capire perché né di uscirne. */}
      {(() => {
        const attivi: FiltroAttivo[] = [];
        if (fCorrectedField) {
          attivi.push({
            chiave: "corrected_field",
            etichetta: "Campo corretto",
            valore: correctedFieldLabel(fCorrectedField),
          });
        }
        if (fHadCorrections) {
          attivi.push({
            chiave: "had_corrections",
            etichetta: "Modifiche manuali",
            valore: fHadCorrections === "true" ? "Con modifiche" : "Senza modifiche",
          });
        }
        // Il periodo compare fra le chip **solo qui**: questa lista non ha un
        // selettore di date (nessun DateRangePicker), quindi senza la chip la
        // restrizione temporale che arriva da ogni drill-down resterebbe del tutto
        // invisibile. Sulla lista RDI le date non sono chip perché lì il selettore
        // esiste e le mostra già: duplicarle sarebbe rumore su ogni drill.
        if (fDateFrom || fDateTo) {
          attivi.push({
            chiave: "date_from",
            etichetta: "Periodo",
            valore: fDateFrom && fDateTo ? `${fDateFrom} → ${fDateTo}` : fDateFrom || `fino al ${fDateTo}`,
          });
        }
        return (
          <ActiveFiltersBar
            filtri={attivi}
            onRemove={(chiavi) =>
              setFilter(
                Object.fromEntries(
                  // La chip del periodo ne rappresenta due: rimuoverla deve
                  // azzerare entrambi gli estremi, non lasciarne uno appeso.
                  chiavi.flatMap((k) => (k === "date_from" ? [["date_from", null], ["date_to", null]] : [[k, null]])),
                ),
              )
            }
          />
        );
      })()}

      {isLoading && !data && <div className="loading-center"><div className="spinner spinner-lg" /></div>}

      {error && (
        <div className="card" style={{ background: "var(--indaco-danger-light)", color: "var(--indaco-danger)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span> Errore: {error}
        </div>
      )}

      {/* Table */}
      {data && (
        <div className="card" style={{ padding: 0, opacity: isLoading ? 0.6 : 1, transition: "opacity 150ms" }}>
          <div className="table-container">
            <table style={{ tableLayout: "fixed", width: "100%", minWidth: 1080 }}>
              <thead>
                <tr>
                  <ColHeader label="" sortKey="status" filterKey="status" filterType="select"
                    filterValue={status} options={STATUS_OPTIONS} style={{ width: 40, textAlign: "center" }} />
                  <ColHeader label="Tipo" sortKey="document_type" filterKey="document_type" filterType="select"
                    filterValue={fDocumentType} options={DOCUMENT_TYPE_OPTIONS} style={{ width: 70, textAlign: "center" }} />
                  <ColHeader label="ID" sortKey="id" filterKey="id" filterType="text" filterValue={fId}
                    style={{ width: 70 }} />
                  <ColHeader label="PDF" sortKey="date" filterKey="pdf_filename" filterType="text"
                    filterValue={fPdf} style={{ width: 170 }} />
                  <ColHeader label="N. DDT" sortKey="numero_ddt" filterKey="numero_ddt" filterType="text"
                    filterValue={fNumeroDdt} style={{ width: 110 }} />
                  <ColHeader label="ODA" sortKey="ordine_acquisto" filterKey="ordine_acquisto" filterType="text"
                    filterValue={fOda} style={{ width: 100 }} />
                  <ColHeader label="Cod. Fornitore" sortKey="fornitore_pk" filterKey="fornitore_pk" filterType="text"
                    filterValue={fFornitorePk} style={{ width: 110 }} />
                  <ColHeader label="Fornitore" sortKey="fornitore" filterKey="fornitore" filterType="text"
                    filterValue={fFornitore} style={{ width: 160 }} />
                  <th style={{ width: 50, textAlign: "center", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-gray-600)" }}>Righe</th>
                  <th style={{ width: 52, textAlign: "right", fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-gray-600)" }}>Compl.</th>
                  <ColHeader label="Data doc." sortKey="doc_date" style={{ width: 90 }} />
                  <ColHeader label="Ricevuta" sortKey="date" style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={12} style={{ textAlign: "center", padding: "var(--space-10)", color: "var(--indaco-gray-400)" }}>
                      Nessuna pratica trovata
                    </td>
                  </tr>
                )}
                {data.items.map((p) => {
                  const comp = ddtCompleteness(p.ddt_data);
                  const ddt = p.ddt_data;
                  return (
                    <tr key={p.id}
                      style={p.id === lastVisitedId ? { background: "var(--indaco-brand-primary-light)" } : undefined}
                      onClick={() => { setLastVisitedId(p.id); localStorage.setItem("lastVisitedDdtId", p.id); goToPractice(p.id); }}>
                      <td style={{ textAlign: "center", overflow: "hidden" }}>
                        {getStatusIcon(p.status)}
                        {p.is_duplicate && (
                          <span className="badge badge-warning" style={{ marginLeft: 2, fontSize: "var(--font-size-xs)", padding: "0 4px" }} title={`Duplicato di ${p.duplicate_of}`}>D</span>
                        )}
                      </td>
                      <td style={{ textAlign: "center", overflow: "hidden" }}>
                        {getDocumentTypeBadge(p.document_type)}
                      </td>
                      <td style={{ overflow: "hidden" }}>
                        <code className="truncate" style={{ display: "block", color: "var(--indaco-gray-600)",
                          fontSize: "var(--font-size-xs)" }} title={p.id}>{p.id.slice(0, 8)}</code>
                      </td>
                      <td style={{ overflow: "hidden" }} title={p.pdf_filename ?? undefined}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                          <span className="truncate" style={{ flex: 1, fontSize: "var(--font-size-xs)" }}>
                            {p.pdf_filename ? p.pdf_filename.replace(/\.pdf$/i, "") : "—"}
                          </span>
                          {p.total_splits > 1 && (
                            <span
                              className="badge badge-info"
                              style={{ flexShrink: 0, fontSize: 10, padding: "0 5px", fontFamily: "monospace" }}
                              title={`DDT ${p.split_index} di ${p.total_splits} nel PDF`}
                            >
                              {p.split_index}/{p.total_splits}
                            </span>
                          )}
                          <LockChip lockedBy={p.locked_by} lockedByName={p.locked_by_name} lockedAt={p.locked_at} />
                        </div>
                      </td>
                      <td style={{ overflow: "hidden" }}>
                        <code className="truncate" style={{ display: "block", fontFamily: "monospace", fontSize: "var(--font-size-sm)" }} title={ddt?.numero_ddt ?? ""}>
                          {ddt?.numero_ddt ?? "—"}
                        </code>
                      </td>
                      <td style={{ overflow: "hidden" }}>
                        <span className="truncate" style={{ display: "block", fontSize: "var(--font-size-sm)" }} title={ddt?.ordine_acquisto ?? ""}>
                          {ddt?.ordine_acquisto ?? "—"}
                        </span>
                      </td>
                      <td style={{ overflow: "hidden" }}>
                        <span style={{ fontFamily: "monospace", fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-700)", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {p.fornitore_pk ?? "—"}
                          {p.fornitore_sim != null && (
                            <span style={{ marginLeft: 4, color: fornitorePkSimColor(p.fornitore_sim), fontWeight: 600 }}>
                              {Math.round(p.fornitore_sim * 100)}%
                            </span>
                          )}
                        </span>
                      </td>
                      <td style={{ overflow: "hidden" }}>
                        <span className="truncate" style={{ display: "block", fontSize: "var(--font-size-sm)" }} title={(ddt?.fornitore_testo_ocr ?? ddt?.fornitore_testo) ?? ""}>
                          {(ddt?.fornitore_testo_ocr ?? ddt?.fornitore_testo) ?? "—"}
                        </span>
                      </td>
                      <td style={{ textAlign: "center", fontSize: "var(--font-size-sm)" }}>
                        {ddt?.righe?.length ?? 0}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {(p.status === "pending_review" || p.status === "approved") ? (
                          <span style={{ fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", color: completenessColor(comp) }}>
                            {Math.round(comp * 100)}%
                          </span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      <td style={{ whiteSpace: "nowrap", overflow: "hidden" }} className="text-sm">
                        <span title="Data del documento DDT">{ddt?.data_ora || "—"}</span>
                      </td>
                      <td style={{ whiteSpace: "nowrap", overflow: "hidden" }} className="text-sm">
                        {p.email_received_at
                          ? <span title={formatDateFull(p.email_received_at)}>{formatDate(p.email_received_at)}</span>
                          : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}



      {/* Pagination */}
      {data && data.total > 0 && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "var(--space-3)", gap: "var(--space-2)" }}>
          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setFilter({ page: page === 2 ? null : String(page - 1) })}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_left</span>
              </button>
              {(() => {
                const pages: (number | "...")[] = [];
                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  pages.push(1);
                  if (page > 3) pages.push("...");
                  const start = Math.max(2, page - 1);
                  const end = Math.min(totalPages - 1, page + 1);
                  for (let i = start; i <= end; i++) pages.push(i);
                  if (page < totalPages - 2) pages.push("...");
                  pages.push(totalPages);
                }
                return pages.map((p, i) =>
                  p === "..." ? <span key={`e${i}`} className="text-muted" style={{ padding: "0 4px" }}>…</span>
                    : <button key={p} className={page === p ? "active" : ""}
                        onClick={() => setFilter({ page: p === 1 ? null : String(p) })}>{p}</button>
                );
              })()}
              <button disabled={page >= totalPages} onClick={() => setFilter({ page: String(page + 1) })}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chevron_right</span>
              </button>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--font-size-sm)" }}>
            <span className="text-muted">Mostra</span>
            <select className="form-input" value={pageSize}
              style={{ width: 70, padding: "2px 6px", fontSize: "var(--font-size-sm)" }}
              onChange={(e) => setFilter({ size: e.target.value === String(DEFAULT_PAGE_SIZE) ? null : e.target.value, page: null })}>
              {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="text-muted">di {data.total}</span>
          </div>
        </div>
      )}
    </div>
  );
}
